import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, FolderOpen, Plus, Cloud, LogIn, Menu, X, Archive, RotateCcw } from 'lucide-react';
import { useProjectStore } from '../stores/projectStore';
import { startActivityCleanup, stopActivityCleanup, startTerminalActivityTracking, stopTerminalActivityTracking } from '../stores/projectActivityStore';
import { useTerminalStore } from '../stores/terminalStore';
import { startClaudeActivityPolling, stopClaudeActivityPolling } from '../stores/claudeActivityStore';
import ProjectActivityDot from './ProjectActivityDot';
import { useBackend } from '../services/backend';
import { useMachineStore } from '../stores/machineStore';
import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';
import { isWeb } from '../lib/platform';
import {
  sortProjects,
  partitionByActive,
  groupByTeam,
  isProjectActive,
  openTerminalPaths,
  PROJECT_DND_MIME,
  UNGROUPED_ID,
} from '../lib/projectSort';
import { getProjectGroupId, setProjectGroupId } from '../lib/projectSettings';
import { useGroupStore } from '../stores/groupStore';
import MachineSelector from './MachineSelector';
import SortControl from './SortControl';
import NpmScriptsPanel from './NpmScriptsPanel';
import type { Project } from '../types/tauri';

// Context menu state
interface ContextMenuState {
  x: number;
  y: number;
  project: Project;
}

// Per-project accent color classes (hoisted so it isn't rebuilt per row).
const COLOR_CLASSES: Record<string, { bg: string; text: string; border: string }> = {
  purple: { bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500' },
  blue: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500' },
  green: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500' },
  orange: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500' },
  red: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500' },
  yellow: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500' },
  cyan: { bg: 'bg-cyan-500/20', text: 'text-cyan-400', border: 'border-cyan-500' },
  pink: { bg: 'bg-pink-500/20', text: 'text-pink-400', border: 'border-pink-500' },
  indigo: { bg: 'bg-indigo-500/20', text: 'text-indigo-400', border: 'border-indigo-500' },
  teal: { bg: 'bg-teal-500/20', text: 'text-teal-400', border: 'border-teal-500' },
};

export default function ProjectSidebar() {
  const [isCollapsed, setIsCollapsed] = useState(false);
  // Collapse states persist across reloads/restarts (see uiStore).
  const archiveExpanded = useUIStore((state) => state.archivedExpanded);
  const toggleArchived = useUIStore((state) => state.toggleArchived);
  const inactiveExpanded = useUIStore((state) => state.inactiveGroupExpanded);
  const toggleInactiveGroup = useUIStore((state) => state.toggleInactiveGroup);
  const collapsedTeams = useUIStore((state) => state.collapsedTeams);
  const toggleTeamCollapsed = useUIStore((state) => state.toggleTeamCollapsed);
  const groups = useGroupStore((state) => state.groups);
  const loadGroups = useGroupStore((state) => state.loadGroups);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const isMobileOpen = useUIStore((state) => state.mobileSidebarOpen);
  const setMobileSidebarOpen = useUIStore((state) => state.setMobileSidebarOpen);
  const navigate = useNavigate();
  const location = useLocation();
  const projects = useProjectStore((state) => state.projects);
  const updateProject = useProjectStore((state) => state.updateProject);
  const sortMode = useUIStore((state) => state.projectSortMode);
  const sortDir = useUIStore((state) => state.projectSortDir);
  const sidebarPosition = useUIStore((state) => state.sidebarPosition);

  // On workspace routes, MobileHeader provides the hamburger — hide the floating one
  const isWorkspaceRoute = location.pathname.startsWith('/workspace/');

  // Re-render (and re-sort) when terminals open/close so the "active" sort and
  // the per-project activity dots stay current. The dots subscribe to activity
  // themselves; this keeps the list ordering live.
  useTerminalStore((state) => state.sessions);

  const backend = useBackend();

  // Machine store for multi-machine support
  const connectionState = useMachineStore((state) => state.connectionState);
  const isRemoteMode = useMachineStore((state) => state.isRemoteMode);
  const getSelectedMachine = useMachineStore((state) => state.getSelectedMachine);
  const selectedMachineId = useMachineStore((state) => state.selectedMachineId);
  // Auth store for web mode
  const { isAuthenticated, setShowAuthDialog } = useAuthStore();

  // Check if we're in web mode
  const webMode = isWeb();

  // In web mode, check if user has selected a remote machine
  const hasSelectedRealMachine = webMode
    ? selectedMachineId !== null
    : true;

  // Get current project ID from URL
  const currentProjectId = location.pathname.startsWith('/workspace/')
    ? location.pathname.split('/')[2]
    : null;

  // Start/stop the activity cleanup interval
  useEffect(() => {
    startActivityCleanup();
    return () => stopActivityCleanup();
  }, []);

  useEffect(() => { void loadGroups(); }, [loadGroups]);

  // Wire terminal output to activity indicators
  useEffect(() => {
    startTerminalActivityTracking(backend);
    return () => stopTerminalActivityTracking();
  }, [backend]);

  // Poll Claude Code session files for activity detection
  useEffect(() => {
    startClaudeActivityPolling();
    return () => stopClaudeActivityPolling();
  }, []);


  // Close mobile drawer on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [location.pathname, setMobileSidebarOpen]);

  const handleProjectClick = (projectId: string) => {
    navigate(`/workspace/${projectId}`);
  };

  const setCurrentProject = useProjectStore((state) => state.setCurrentProject);
  const handleProjectsClick = () => {
    setCurrentProject(null);
    navigate('/');
  };

  const handleAddProject = () => {
    if (webMode && !hasSelectedRealMachine) return;
    useUIStore.getState().openAddProjectDialog();
  };

  const handleSignIn = () => {
    setShowAuthDialog(true);
  };

  const toggleMobile = useUIStore((state) => state.toggleMobileSidebar);

  const isRight = sidebarPosition === 'right';

  // One project row — shared by the Active and Inactive groups.
  const renderProjectRow = (project: Project) => {
    const projectColor = project.color && COLOR_CLASSES[project.color] ? COLOR_CLASSES[project.color] : null;
    const isActive = currentProjectId === project.id;
    return (
      <button
        key={project.id}
        draggable={hasTeams}
        onDragStart={hasTeams ? (e) => { e.dataTransfer.setData(PROJECT_DND_MIME, project.id); e.dataTransfer.effectAllowed = 'move'; } : undefined}
        onClick={() => handleProjectClick(project.id)}
        onContextMenu={(e) => handleContextMenu(e, project)}
        className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
          isActive
            ? projectColor
              ? `${projectColor.bg} ${projectColor.text} border-l-4 ${projectColor.border}`
              : 'bg-primary text-primary-content'
            : projectColor
              ? `hover:${projectColor.bg} border-l-4 border-transparent hover:${projectColor.border}`
              : 'hover:bg-base-300'
        }`}
        title={project.name}
      >
        <span className="relative inline-flex flex-shrink-0">
          {project.icon?.startsWith('data:') ? (
            <img src={project.icon} alt="" className="w-5 h-5 rounded object-cover" />
          ) : project.icon ? (
            <span className="text-lg w-5 h-5 flex items-center justify-center">
              {project.icon}
            </span>
          ) : (
            <FolderOpen className={`w-5 h-5 ${isActive && projectColor ? projectColor.text : ''}`} />
          )}
          <ProjectActivityDot projectPath={project.root_path} />
        </span>
        {!isCollapsed && (
          <div className="flex-1 text-left min-w-0">
            <div className="text-sm font-medium truncate">{project.name}</div>
            <div className={`text-xs truncate ${
              isActive
                ? projectColor ? `${projectColor.text}/70` : 'text-primary-content/70'
                : 'text-base-content/60'
            }`}>
              {project.root_path}
            </div>
          </div>
        )}
      </button>
    );
  };

  // Static header for the always-open Active group. The leading spacer matches
  // the Inactive header's chevron width so the dots + labels line up.
  const renderGroupHeader = (label: string, count: number, tone: 'active' | 'inactive') => (
    <div className="flex items-center gap-2 px-3 pt-2 pb-1">
      <span className="w-3.5 flex-shrink-0" />
      <span className={`w-1.5 h-1.5 rounded-full ${tone === 'active' ? 'bg-emerald-500' : 'bg-base-content/30'}`} />
      <span className={`text-xs font-semibold uppercase tracking-wider ${
        tone === 'active' ? 'text-emerald-500/80' : 'text-base-content/40'
      }`}>
        {label} ({count})
      </span>
    </div>
  );

  // Filter out archived projects (they get their own collapsible section).
  const nonArchivedProjects = projects.filter((p) => p.status !== 'archived');
  const archivedProjects = projects.filter((p) => p.status === 'archived');

  // Sort within-mode/direction first, THEN split into Active (has an open
  // terminal) / Inactive groups — filtering preserves the sorted order, so each
  // group is independently ordered by the chosen sort. The useTerminalStore
  // subscription above keeps this partition live as terminals open/close.
  const sortedNonArchived = sortProjects(nonArchivedProjects, sortMode, sortDir);
  const { active: activeGroup, inactive: inactiveGroup } = partitionByActive(sortedNonArchived);
  const sortedArchivedProjects = sortProjects(archivedProjects, sortMode, sortDir);

  const hasTeams = groups.length > 0;
  const openPaths = openTerminalPaths();

  // Reassign a project's team (persisted in its settings blob). No-op if same.
  const assignToTeam = async (projectId: string, teamId: string | null) => {
    const p = projects.find((x) => x.id === projectId);
    if (!p || getProjectGroupId(p.settings) === teamId) return;
    try {
      await updateProject(projectId, { settings: setProjectGroupId(p.settings, teamId) });
    } catch { /* store logs it */ }
  };
  const onDropToTeam = (e: React.DragEvent, teamId: string | null) => {
    e.preventDefault();
    setDragOverKey(null);
    const id = e.dataTransfer.getData(PROJECT_DND_MIME);
    if (id) void assignToTeam(id, teamId);
  };

  // Context menu handlers
  const handleContextMenu = (e: React.MouseEvent, project: Project) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ x: e.clientX, y: e.clientY, project });
  };

  const closeContextMenu = () => setContextMenu(null);

  const handleArchive = async () => {
    if (contextMenu) {
      await updateProject(contextMenu.project.id, { status: 'archived' });
      closeContextMenu();
    }
  };

  const handleRestore = async () => {
    if (contextMenu) {
      await updateProject(contextMenu.project.id, { status: 'active' });
      closeContextMenu();
    }
  };

  // Close context menu on click outside or Escape
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = () => closeContextMenu();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeContextMenu();
    };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [contextMenu]);

  return (
    <>
      {/* Mobile hamburger button — hidden on workspace routes where MobileHeader provides it */}
      {!isWorkspaceRoute && (
        <button
          onClick={toggleMobile}
          className={`md:hidden fixed top-3 ${isRight ? 'right-3' : 'left-3'} z-50 btn btn-ghost btn-sm btn-square bg-base-200 shadow-md`}
          aria-label="Toggle sidebar"
        >
          {isMobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      )}

      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed ${isRight ? 'right-0' : 'left-0'} top-0 bottom-0 z-40 bg-base-200 ${isRight ? 'border-l' : 'border-r'} border-base-300 transition-all duration-300
          ${isCollapsed ? 'w-20' : 'w-64'}
          ${isMobileOpen ? 'translate-x-0' : isRight ? 'translate-x-full' : '-translate-x-full'} md:translate-x-0
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-base-300">
            {!isCollapsed && (
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-base-content">Projects</h2>
                {isRemoteMode() && (
                  <span className="badge badge-info badge-xs gap-1">
                    <Cloud className="w-3 h-3" />
                    {getSelectedMachine()?.name || 'Remote'}
                  </span>
                )}
              </div>
            )}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="btn btn-ghost btn-xs btn-square"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? (
                isRight ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
              ) : (
                isRight ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Machine Selector - shown in web mode or when connected to server */}
          {(webMode || connectionState === 'authenticated') && (
            <MachineSelector isCollapsed={isCollapsed} />
          )}

          {/* Sign In prompt for web mode when not authenticated */}
          {webMode && !isAuthenticated && !isCollapsed && (
            <div className="px-4 py-2 border-b border-base-300">
              <button
                onClick={handleSignIn}
                className="btn btn-primary btn-sm w-full gap-2"
              >
                <LogIn className="w-4 h-4" />
                Sign In
              </button>
            </div>
          )}

          {/* Navigation Items */}
          <div className="flex-1 overflow-y-auto p-2">
            {/* Projects header with + button */}
            <div className="flex items-center justify-between px-3 py-2">
              <button
                onClick={handleProjectsClick}
                className="flex items-center gap-2 hover:text-primary transition-colors"
                title="All Projects"
              >
                <FolderOpen className="w-4 h-4 flex-shrink-0" />
                {!isCollapsed && <span className="text-xs font-semibold uppercase tracking-wider text-base-content/60">Projects</span>}
              </button>
              {!isCollapsed && (
                <div className={webMode && !hasSelectedRealMachine ? 'tooltip tooltip-left' : ''} data-tip="Select a machine first">
                  <button
                    onClick={handleAddProject}
                    disabled={webMode && !hasSelectedRealMachine}
                    className={`btn btn-ghost btn-xs btn-square ${webMode && !hasSelectedRealMachine ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title="Add project"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Sort control */}
            {!isCollapsed && projects.length > 1 && (
              <div className="px-3 pb-2">
                <SortControl />
              </div>
            )}

            {/* Projects grouped by status: Active (open terminal) then Inactive.
                Sorting reorders WITHIN each group only. Headers hide when the
                sidebar is collapsed or a group is empty. When Teams are defined
                (and the rail is expanded), projects are grouped by team first. */}
            {!(hasTeams && !isCollapsed) && (
              <div className="space-y-1">
                {activeGroup.length > 0 && (
                  <div className="space-y-1">
                    {!isCollapsed && renderGroupHeader('Active', activeGroup.length, 'active')}
                    {activeGroup.map(renderProjectRow)}
                  </div>
                )}
                {inactiveGroup.length > 0 && (
                  <div className="space-y-1">
                    {!isCollapsed ? (
                      <>
                        {/* Inactive is collapsible so it can be tucked away. */}
                        <button
                          onClick={toggleInactiveGroup}
                          className="flex items-center gap-2 px-3 pt-2 pb-1 w-full text-left hover:bg-base-300/40 rounded-lg transition-colors"
                          title={inactiveExpanded ? 'Collapse inactive' : 'Expand inactive'}
                        >
                          <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-base-content/40 transition-transform ${inactiveExpanded ? '' : '-rotate-90'}`} />
                          <span className="w-1.5 h-1.5 rounded-full bg-base-content/30" />
                          <span className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                            Inactive ({inactiveGroup.length})
                          </span>
                        </button>
                        {inactiveExpanded && inactiveGroup.map(renderProjectRow)}
                      </>
                    ) : (
                      inactiveGroup.map(renderProjectRow)
                    )}
                  </div>
                )}
              </div>
            )}

            {hasTeams && !isCollapsed && (
              <div className="space-y-1">
                {groupByTeam(sortedNonArchived, groups, (p) => isProjectActive(p, openPaths)).map((bucket) => {
                  const key = bucket.group?.id ?? UNGROUPED_ID;
                  const name = bucket.group?.name ?? 'Ungrouped';
                  const color = bucket.group?.color ?? null;
                  const count = bucket.active.length + bucket.inactive.length;
                  const collapsed = collapsedTeams.includes(key);
                  const dropping = dragOverKey === key;
                  return (
                    <div
                      key={key}
                      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverKey !== key) setDragOverKey(key); }}
                      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverKey((k) => (k === key ? null : k)); }}
                      onDrop={(e) => onDropToTeam(e, bucket.group?.id ?? null)}
                      className={`rounded-lg transition-colors ${dropping ? 'ring-2 ring-primary/60 bg-primary/5' : ''}`}
                    >
                      <button
                        onClick={() => toggleTeamCollapsed(key)}
                        className="flex items-center gap-2 px-2 pt-2 pb-1 w-full text-left hover:bg-base-300/40 rounded-lg transition-colors"
                        title={collapsed ? `Expand ${name}` : `Collapse ${name}`}
                      >
                        <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 text-base-content/50 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
                        <span className="w-2 h-2 rounded-full shrink-0 border border-base-content/20" style={{ backgroundColor: color ?? 'transparent' }} />
                        <span className="text-xs font-bold uppercase tracking-wider text-base-content/70 truncate">{name}</span>
                        <span className="text-[10px] text-base-content/40">({count})</span>
                      </button>
                      {!collapsed && (
                        <div className="pl-1 space-y-1">
                          {bucket.active.length > 0 && (
                            <>
                              {renderGroupHeader('Active', bucket.active.length, 'active')}
                              {bucket.active.map(renderProjectRow)}
                            </>
                          )}
                          {bucket.inactive.length > 0 && (
                            <>
                              {renderGroupHeader('Inactive', bucket.inactive.length, 'inactive')}
                              {bucket.inactive.map(renderProjectRow)}
                            </>
                          )}
                          {count === 0 && <div className="px-3 py-1.5 text-xs text-base-content/40 italic">Drag a project here</div>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Empty State */}
            {nonArchivedProjects.length === 0 && !isCollapsed && (
              <div className="text-center py-8 px-4">
                <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p className="text-xs text-base-content/60">No projects yet</p>
                <p className="text-xs text-base-content/50 mt-1">
                  Create your first project to get started
                </p>
              </div>
            )}

            {/* NPM Scripts Panel */}
            <NpmScriptsPanel isCollapsed={isCollapsed} />

            {/* Archived Section */}
            {archivedProjects.length > 0 && !isCollapsed && (
              <div className="mt-4 pt-4 border-t border-base-300">
                <button
                  onClick={toggleArchived}
                  className="flex items-center gap-2 px-3 py-2 w-full text-left hover:bg-base-300 rounded-lg transition-colors"
                >
                  <ChevronDown className={`w-4 h-4 text-base-content/50 transition-transform ${archiveExpanded ? '' : '-rotate-90'}`} />
                  <Archive className="w-4 h-4 text-base-content/40" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-base-content/40">
                    Archived ({archivedProjects.length})
                  </span>
                </button>

                {archiveExpanded && (
                  <div className="mt-1 space-y-1 opacity-60">
                    {sortedArchivedProjects.map((project) => (
                      <button
                        key={project.id}
                        onClick={() => handleProjectClick(project.id)}
                        onContextMenu={(e) => handleContextMenu(e, project)}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors hover:bg-base-300"
                        title={`${project.name} (archived)`}
                      >
                        {project.icon?.startsWith('data:') ? (
                          <img src={project.icon} alt="" className="w-5 h-5 rounded object-cover flex-shrink-0 grayscale" />
                        ) : project.icon ? (
                          <span className="text-lg flex-shrink-0 w-5 h-5 flex items-center justify-center opacity-50">
                            {project.icon}
                          </span>
                        ) : (
                          <FolderOpen className="w-5 h-5 flex-shrink-0 text-base-content/40" />
                        )}
                        <div className="flex-1 text-left min-w-0">
                          <div className="text-sm font-medium truncate text-base-content/60">{project.name}</div>
                          <div className="text-xs truncate text-base-content/40">
                            {project.root_path}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Spacer to push content (hidden on mobile where sidebar is an overlay) */}
      <div className={`hidden md:block ${isCollapsed ? 'w-20' : 'w-64'} flex-shrink-0`} />

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50 bg-base-200 border border-base-300 rounded-lg shadow-xl py-1 min-w-[160px]"
          style={{
            left: Math.min(contextMenu.x, window.innerWidth - 180),
            top: Math.min(contextMenu.y, window.innerHeight - 100),
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.project.status === 'archived' ? (
            <button
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors"
              onClick={handleRestore}
            >
              <RotateCcw className="w-4 h-4 text-base-content/60" />
              Restore
            </button>
          ) : (
            <button
              className="flex items-center gap-2 w-full px-3 py-2 text-sm text-left hover:bg-base-300 transition-colors"
              onClick={handleArchive}
            >
              <Archive className="w-4 h-4 text-base-content/60" />
              Archive
            </button>
          )}
          {hasTeams && contextMenu.project.status !== 'archived' && (
            <div className="border-t border-base-300 mt-1 pt-1 max-h-[40vh] overflow-y-auto">
              <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-base-content/40">Move to team</div>
              {groups.map((g) => {
                const current = getProjectGroupId(contextMenu.project.settings) === g.id;
                return (
                  <button
                    key={g.id}
                    className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-left hover:bg-base-300 transition-colors"
                    onClick={() => { void assignToTeam(contextMenu.project.id, g.id); closeContextMenu(); }}
                  >
                    <span className="w-2 h-2 rounded-full shrink-0 border border-base-content/20" style={{ backgroundColor: g.color ?? 'transparent' }} />
                    <span className="flex-1 truncate">{g.name}</span>
                    {current && <span className="text-primary text-xs">✓</span>}
                  </button>
                );
              })}
              <button
                className="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-left hover:bg-base-300 transition-colors"
                onClick={() => { void assignToTeam(contextMenu.project.id, null); closeContextMenu(); }}
              >
                <span className="w-2 h-2 rounded-full shrink-0 border border-base-content/20" />
                <span className="flex-1 truncate text-base-content/70">Ungrouped</span>
                {getProjectGroupId(contextMenu.project.settings) === null && <span className="text-primary text-xs">✓</span>}
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
